import React from 'react';
import { Check, Circle, Clock, FileText, Send, Globe, CheckCircle, ExternalLink } from 'lucide-react';

const statusSteps = [
  { key: 'paid', label: 'Payment Confirmed', icon: Check, description: 'Your payment has been verified' },
  { key: 'content_received', label: 'Content Received', icon: FileText, description: 'Your content has been submitted' },
  { key: 'under_review', label: 'Under Editorial Review', icon: Clock, description: 'Content being reviewed by our team' },
  { key: 'submitted_to_publisher', label: 'Submitted to Publisher', icon: Send, description: 'Sent to publication editorial team' },
  { key: 'published', label: 'Article Published', icon: Globe, description: 'Your article is now live!' },
  { key: 'completed', label: 'Order Completed', icon: CheckCircle, description: 'Campaign successfully completed' }
];

// Map legacy statuses to new ones
const statusAliases = {
  'live': 'published'
};

const OrderTimeline = ({ currentStatus, statusUpdates = [], liveUrl }) => {
  // Handle legacy status names
  const normalizedStatus = statusAliases[currentStatus] || currentStatus;
  const currentIndex = statusSteps.findIndex(step => step.key === normalizedStatus);

  return (
    <div className="relative" data-testid="order-timeline">
      {/* Progress Line Background */}
      <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-[#e5e5e5]" />
      
      {/* Progress Line Fill */}
      <div 
        className="absolute left-5 top-5 w-0.5 bg-[#D4AF37] transition-all duration-500"
        style={{ height: `${Math.max(0, (currentIndex / (statusSteps.length - 1)) * 100)}%` }}
      />

      <div className="space-y-0">
        {statusSteps.map((step, index) => {
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          const isPending = index > currentIndex;
          const update = statusUpdates.find(u => u.status === step.key || statusAliases[u.status] === step.key);
          const Icon = step.icon;

          return (
            <div 
              key={step.key} 
              className={`relative flex gap-4 pb-8 last:pb-0 ${isPending ? 'opacity-50' : ''}`}
              data-testid={`timeline-step-${step.key}`}
            >
              {/* Icon Circle */}
              <div className="relative z-10">
                <div 
                  className={`w-10 h-10 flex items-center justify-center transition-all duration-300 ${
                    isCompleted 
                      ? 'bg-[#D4AF37] text-[#0d0d0d]' 
                      : 'bg-white border-2 border-[#e5e5e5] text-[#a3a3a3]'
                  } ${isCurrent ? 'ring-4 ring-[#D4AF37]/20' : ''}`}
                >
                  <Icon className="w-5 h-5" />
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 pt-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className={`font-manrope font-semibold text-sm ${
                    isCompleted ? 'text-[#1a1a1a]' : 'text-[#a3a3a3]'
                  }`}>
                    {step.label}
                  </h4>
                  {isCurrent && (
                    <span className="px-2 py-0.5 bg-[#D4AF37] text-[#0d0d0d] text-[10px] font-manrope uppercase tracking-wider">
                      Current
                    </span>
                  )}
                </div>
                
                <p className={`font-manrope text-xs mb-2 ${
                  isCompleted ? 'text-[#737373]' : 'text-[#a3a3a3]'
                }`}>
                  {step.description}
                </p>
                
                {update && (
                  <div className="mt-2 p-3 bg-[#fafafa] border border-[#e5e5e5]">
                    {update.message && (
                      <p className="font-manrope text-sm text-[#525252] mb-1">{update.message}</p>
                    )}
                    <p className="font-mono text-[10px] text-[#a3a3a3] uppercase">
                      {new Date(update.created_at).toLocaleString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
                
                {/* Live URL Display */}
                {(step.key === 'published' || step.key === 'completed') && liveUrl && isCompleted && (
                  <a 
                    href={liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="live-url-link"
                    className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-all"
                  >
                    <Globe className="w-4 h-4" />
                    View Live Article
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderTimeline;
